import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { useQuery } from "@tanstack/react-query";

//Services
import {
  VIEW_DIVISION_QUERY_KEY,
  divisionService,
} from "../../../../../Services/divisionService";
import {
  DEPARTMENT_QUERY_KEY,
  VIEW_DEPARTMENT_QUERY_KEY,
  departmentService,
} from "../../../../../Services/departmentService";

//Types
import { ViewDivision } from "../../../../../Types/CompanyProfile/Division";

const INITIAL_STATE = {
  division_id: 0,
  name: "",
  status: -1,
  created_by: undefined,
  updated_by: undefined,
};

interface CreateModalProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateDepartmentForm: React.FC<CreateModalProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  //GetAllDivision
  const QueryKey = [...VIEW_DIVISION_QUERY_KEY, company_code];

  const { data: DivisionData = [], isLoading: isDivisionLoading } = useQuery<
    ViewDivision[]
  >({
    queryKey: QueryKey,
    queryFn: () =>
      divisionService.getAllViewDivisionByCompany(company_code as string),
  });

  const DivisionOptions = DivisionData.filter((item) => item.status === 1).map(
    (item) => ({
      value: item.id,
      label: item.name,
    }),
  );

  const createDepartmentMutation = useMutation({
    mutationFn: departmentService.createDepartment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_DEPARTMENT_QUERY_KEY });

      toast.success("Department has been created successfully.");

      setFormData(INITIAL_STATE);

      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create department. Please verify the entered information.",
        );
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSelectedDivision = (division_id: number) => {
    setFormData((prev) => ({ ...prev, division_id: division_id }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
      created_by: user.id,
      updated_by: user.id,
      company_code: company_code,
    };
    createDepartmentMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Division<b className="text-danger">*</b>
            </Label>
            <Input
              value={formData.division_id}
              id="division_id"
              name="division_id"
              type="select"
              disabled={isDivisionLoading}
              onChange={(e) => handleSelectedDivision(Number(e.target.value))}
            >
              {isDivisionLoading ? (
                <option value="">Division Loading...</option>
              ) : (
                <>
                  <option disabled value={0}>
                    Select Division
                  </option>
                  {DivisionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </>
              )}
            </Input>
          </Col>

          <Col md="12">
            <Label>
              Department Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter New Department"
              maxLength={150}
              minLength={3}
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createDepartmentMutation.isPending}
            >
              {createDepartmentMutation.isPending
                ? "Creating Department..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateDepartmentForm;
